import React, { useState, useEffect } from "react";
import { isMobileOnly, isIOS } from "react-device-detect";
import {
    MapSectionInnerWrapper,
    MapSectionWrapper,
    MapSectionHoursWrapper,
    HoursOutterWrapper,
    HourColumnOne,
    HourColumnTwo,
    InputWrapper,
    DifferentAddressWrapper,
    HoursTitle,
    MapFormInputField,
} from "./StyledComponents";
import MapSectionHoursBlock from "./MapSectionHourBlock";
import Clock from "../../assets/SVGComponents/Clock";
import Directions from "../../assets/SVGComponents/Directions";
import convertS3ImageUrl from "../../assets/store/convertS3ImageUrl";
import formatPhoneNumber from "../../assets/store/formatPhoneNumber";
import { formatText } from "../../assets/store/formatText";

const REACT_APP_MAP_API_KEY = "AIzaSyA6juNfY2N1Fu08EP9MowhKxRBVNDkkfEQ";

const MapSection = ({
    client,
    selected_map_section_url,
    use_custom_map_url,
    map_section_url,
    longitude,
    latitude,
    oem,
    dataAttributes,
    rowData,
    map_section_hours,
    testAddress = null,
}) => {
    const {
        city,
        state,
        name,
        address,
        zip,
        dealership_phone,
        conversion_content_overrides,
    } = client;

    const {
        primary_button_color,
        primary_button_text_color,
        primary_hover_button_color,
        primary_hover_button_text_color,
        letter_spacing,
        line_height,
        text_transform,
        border_radius,
        border_color,
    } = oem || {
        primary_button_color: "black",
        primary_button_text_color: "white",
        primary_hover_button_color: "white",
        primary_hover_button_text_color: "black",
    };

    const [partsNumber, setPartsNumber] = useState(null);
    const [serviceNumber, setServiceNumber] = useState(null);
    const [screenSize, setScreenSizes] = useState(
        window?.window?.deviceDetect?.getWindowData() || {
            viewWidth: document.documentElement.clientWidth,
            isMobile: false,
            isDesktop: false,
            isTablet: false,
        }
    );
    const [mapLink, setMapLink] = useState(null);
    const [showDirections, setShowDirections] = useState(false);
    const [userAddress, setUserAddress] = useState(testAddress ?? "");
    const [inputError, setInputError] = useState(null);
    const [loading, setLoading] = useState(null);
    const [addressEngaged, setAddressEngaged] = useState(false);
    let backgroundImage = convertS3ImageUrl(
        "https://fixedopsdigital.s3.amazonaws.com/development/TORQUE-MapRow-Background.jpg",
        { resize: { width: 1280 } }
    );

    const link = use_custom_map_url
        ? map_section_url
            ? map_section_url
            : selected_map_section_url
            ? selected_map_section_url.url
            : null
        : null;

    const serviceHours = map_section_hours?.service;
    const partsHours = map_section_hours?.parts;
    const salesHours = map_section_hours?.sales;

    useEffect(() => {
        // set device detect to track component and provide screen dimension to mapSection local state.
        const key = window.window.deviceDetect.addStateAccessor({
            stateAccessor: setScreenSizes,
        });
        getPhoneNumbers({ phone_numbers: client?.phone_numbers });
        return () => {
            window.window.deviceDetect.removeStateAccessor({ key: key });
        };
    }, []);

    const getPhoneNumbers = ({ phone_numbers = [] }) => {
        if (!phone_numbers) return;
        const actionMap = {
            "Service Phone Number": setServiceNumber,
            "Parts Phone Number": setPartsNumber,
        };

        const numbers = phone_numbers.forEach((number) => {
            if (actionMap[number.name]) actionMap[number.name](number.number);
        });

        return numbers;
    };

    const getDirections = () => {
        if (userAddress === "") {
            setInputError(
                formatText(rowData, "Please enter an address.", false)
            );
            return;
        } else {
            if (screenSize.isMobile && longitude && latitude) {
                window.open(
                    isIOS
                        ? `maps://maps.google.com/maps?daddr=${latitude},${longitude}&amp;ll=`
                        : `https://maps.google.com/maps?daddr=${latitude},${longitude}&amp;ll=`
                );
            } else {
                setLoading(true);
                if (link && use_custom_map_url) setMapLink(link);
                else {
                    let addressStr = `${name} Service, ${city} ${state}`;
                    fetch(
                        `https://www.google.com/maps/embed/v1/directions?key=${REACT_APP_MAP_API_KEY}&origin=${userAddress.replace(
                            " ",
                            "+"
                        )}&destination=${addressStr.replace(" ", "+")}`
                    ).then((data) => {
                        setMapLink(data.url);
                    });
                }
                setLoading(false);
                setShowDirections(true);
            }
        }
    };

    const resetMapSection = () => {
        setUserAddress("");
        setShowDirections(false);
        setMapLink(null);
    };

    const mapContainerStyles = {
        width: "100%",
        height: "100%",
    };

    const submitButtonStyles = {
        background: primary_button_color,
        outline: "none",
        border: border_color ? `1px solid ${border_color}` : "unset",
        borderRadius: border_radius ?? "100px",
        width: screenSize.isMobile ? "90%" : "25%",
        color: primary_button_text_color,
        padding: screenSize.isMobile ? "20px" : "20px 0",
        fontWeight: "bold",
        cursor: "pointer",
        margin: "5px 0",
        fontSize: screenSize.isMobile ? "3vw" : "1em",
        textTransform: text_transform,
    };
    const inputStyles = {
        width: screenSize.isMobile ? "90%" : "100%",
        padding: screenSize.isMobile
            ? "20px 12vw 20px 20px"
            : "20px 12% 20px 20px",
        fontSize: screenSize.isMobile ? "3vw" : "1em",
        marginRight: screenSize.isMobile ? null : "10px",
        borderRadius: border_radius ?? "100px",
    };

    const commonHoursBlockProps = {
        isMobile: screenSize.isMobile,
        hoursBlockStyles: {
            border_radius,
            border_color,
            text_transform,
        },
        rowData,
    };

    const serviceHoursBlock = serviceHours ? (
        <MapSectionHoursBlock
            {...commonHoursBlockProps}
            phoneNumber={serviceNumber}
            hours={serviceHours}
            hoursLabel={"Service Hours"}
            className={"FODServiceHoursSection"}
        />
    ) : null;

    const partsHoursBlock = partsHours ? (
        <MapSectionHoursBlock
            {...commonHoursBlockProps}
            phoneNumber={partsNumber}
            hours={partsHours}
            hoursLabel={"Parts Hours"}
            className={"FODPartsHoursSection"}
        />
    ) : null;

    const salesHoursBlock = salesHours ? (
        <MapSectionHoursBlock
            {...commonHoursBlockProps}
            phoneNumber={dealership_phone}
            hours={salesHours}
            hoursLabel={"Sales Hours"}
            className={"FODSalesHoursSection"}
        />
    ) : null;

    const mapForm = (
        <>
            <div
                className="FODMapFormHeader"
                style={{
                    textAlign: "center",
                    lineHeight: "1.5",
                    padding: "0 10px",
                }}
            >
                <div
                    style={{
                        fontSize: screenSize.isMobile ? "4vw" : "1.5em",
                        letterSpacing: letter_spacing,
                        textTransform: text_transform,
                    }}
                    className="FODMapSectionDirectionsToTitle"
                >
                    {formatText(rowData, "Get Directions to", false)}
                </div>
                <div
                    style={{
                        fontSize: isMobileOnly ? "5vw" : "2em",
                        letterSpacing: letter_spacing,
                        textTransform: text_transform,
                        lineHeight: line_height,
                    }}
                    className="FODMapSectionDealershipName"
                >
                    {conversion_content_overrides?.dealer_name || name}
                </div>
                <div
                    style={{
                        fontSize: screenSize.isMobile ? "4vw" : "1.5em",
                        letterSpacing: letter_spacing,
                    }}
                    className="FODMapSectionDealershipAddress1"
                >
                    {address}
                </div>
                <div
                    style={{ fontSize: screenSize.isMobile ? "4vw" : "1.5em" }}
                    className="FODMapSectionDealershipAddress2"
                >
                    {city}, {state} {zip}
                </div>
                <div style={{ fontSize: screenSize.isMobile ? "3vw" : "1em" }}>
                    {formatPhoneNumber(dealership_phone)}
                </div>
            </div>
            <InputWrapper className="FODMapInputWrapper">
                <div
                    className="FODMapInputWrapper"
                    style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                    }}
                >
                    <MapFormInputField
                        className="FODMapFormInputField"
                        type="text"
                        placeholder={formatText(
                            rowData,
                            "Enter Your Starting Address"
                        )}
                        style={inputStyles}
                        value={userAddress}
                        onChange={(e) => setUserAddress(e.target.value)}
                        data-ascformname={"map_section"}
                        data-ascformtype={"map"}
                        onBlur={() => {
                            if (!addressEngaged) {
                                setAddressEngaged(true);
                                window.dataLayer.push({
                                    event: 'gtm.blur.service-offers-maps',
                                    text: formatText(rowData, "Enter Your Starting Address")
                                });
                            }
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            right: screenSize.isMobile ? "10%" : "5%",
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >
                        <Directions
                            className="FODDirectionsIcon"
                            fill={"#9f9f9f"}
                            svgIconSize={screenSize.isMobile ? "7vw" : "35px"}
                        />
                    </div>
                </div>
                <button
                    {...dataAttributes}
                    className="FODMapSectionSearchButton FODV5GAMapSectionClickEvent"
                    style={submitButtonStyles}
                    onClick={getDirections}
                    data-elementvalue={userAddress}
                    data-ascelementtitle="directions"
                    {...dataAttributes}
                    onMouseOver={(e) => {
                        e.target.style.color = primary_hover_button_text_color;
                        e.target.style.background = primary_hover_button_color;
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.color = primary_button_text_color;
                        e.target.style.background = primary_button_color;
                    }}
                >
                    {formatText(rowData, "Go")}
                </button>
            </InputWrapper>
            {inputError ? (
                <p
                    style={{
                        color: "red",
                        width: "100%",
                        textAlign: "center",
                    }}
                >
                    {inputError}
                </p>
            ) : null}
            <MapSectionHoursWrapper
                mobile={isMobileOnly}
                data-testid="map_dealer_hours_cotainer"
            >
                {serviceHoursBlock ?? salesHoursBlock}
                {partsHoursBlock ??
                    (serviceHoursBlock && salesHoursBlock
                        ? salesHoursBlock
                        : null)}
            </MapSectionHoursWrapper>
        </>
    );

    const directionsMap = (
        <>
            <div
                className="FODMapSectionWrapper"
                id="fod-map-section-container"
                style={mapContainerStyles}
            >
                <iframe
                    title="FODMapSection"
                    className="FODGAMapSection FODMapSectionIFrame"
                    data-testid="directions_map"
                    src={mapLink}
                    width="100%"
                    height="100%"
                    style={{
                        border: 0,
                        borderRadius: "15px",
                        width: "100%",
                        height: "100%",
                        minHeight: "500px",
                    }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
            </div>
        </>
    );

    return (
        <MapSectionWrapper
            className="FODMapSectionWrapper"
            backgroundImage={backgroundImage}
        >
            <MapSectionInnerWrapper directions={showDirections}>
                {showDirections ? directionsMap : mapForm}
            </MapSectionInnerWrapper>
            {showDirections && (
                <DifferentAddressWrapper
                    data-testid="use_another_address"
                    onClick={resetMapSection}
                >
                    {formatText(rowData, "Use Another Address")}...
                </DifferentAddressWrapper>
            )}
        </MapSectionWrapper>
    );
};

export default MapSection;
